import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress
} from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import { commonDialogStyles } from '../common/dialogStyles';

interface QuickAddSetDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (reps: string, weight: string, series: number) => void;
  isSaving: boolean;
  mode?: 'add' | 'edit';
  initialValues?: {
    reps: string;
    weight: string;
    series?: string;
  };
}

export const QuickAddSetDialog: React.FC<QuickAddSetDialogProps> = ({
  open,
  onClose,
  onAdd,
  isSaving,
  mode = 'add',
  initialValues
}) => {
  const { t } = useLanguage();
  const [reps, setReps] = useState('');
  const [weight, setWeight] = useState('');
  const [series, setSeries] = useState('1');
  const [errors, setErrors] = useState<{ reps?: string; weight?: string; series?: string }>({});

  // Reset form when dialog opens/closes or handle initial values
  useEffect(() => {
    if (open) {
      if (initialValues) {
        setReps(initialValues.reps || '');
        setWeight(initialValues.weight || '');
        setSeries(initialValues.series || '1');
      } else {
        setReps('');
        setWeight('');
        setSeries('1');
      }
      setErrors({});
    }
  }, [open, initialValues]);

  const validateForm = () => {
    const newErrors: { reps?: string; weight?: string; series?: string } = {};
    
    if (!reps || isNaN(Number(reps)) || Number(reps) <= 0) {
      newErrors.reps = t('workout.validation.repsRequired');
    }
    
    if (!weight || isNaN(Number(weight)) || Number(weight) < 0) {
      newErrors.weight = t('workout.validation.weightRequired');
    }
    
    if (!series || isNaN(Number(series)) || Number(series) <= 0) {
      newErrors.series = t('workout.validation.seriesRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = () => {
    if (!validateForm()) {
      return;
    }
    
    onAdd(reps, weight, Number(series));
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth 
      sx={commonDialogStyles}
    >
      <DialogTitle>
        {mode === 'edit' ? t('workout.editSet') : t('workout.addSet')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('workout.series')}
            type="number"
            value={series}
            onChange={(e) => setSeries(e.target.value)}
            error={!!errors.series}
            helperText={errors.series}
            fullWidth
            disabled={isSaving}
            inputProps={{ min: 1 }}
          />
          <TextField
            label={t('workout.reps')}
            type="number"
            value={reps}
            onChange={(e) => setReps(e.target.value)}
            error={!!errors.reps}
            helperText={errors.reps}
            fullWidth
            disabled={isSaving}
            inputProps={{ min: 1 }}
          />
          <TextField
            label={t('workout.weight')}
            type="number"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            error={!!errors.weight}
            helperText={errors.weight}
            fullWidth
            disabled={isSaving}
            inputProps={{ min: 0, step: 0.5 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleAdd} 
          variant="contained" 
          disabled={isSaving}
        >
          {isSaving ? (
            <CircularProgress size={20} />
          ) : mode === 'edit' ? (
            t('common.update')
          ) : (
            t('common.create')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 